'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';

interface PageUrlChangeContextType {
  registerRouteListener: (callback: (url: string) => void) => string;
  unregisterRouteListener: (id: string) => void;
}

const PageUrlChangeContext = createContext<PageUrlChangeContextType | undefined>(undefined);

export function usePageURLChangeContext() {
  const context = useContext(PageUrlChangeContext);
  if (!context) {
    throw new Error('usePageURLChangeContext must be used within a PageUrlChangeProvider');
  }
  return context;
}

export const PageUrlChangeProviderClient = ({ children }: { children: React.ReactNode }) => {
  const [routeListeners, setRouteListeners] = useState<Record<string, (url: string) => void>>({});
  const pathname = usePathname();
  // Use window.location.search inside effects rather than useSearchParams() so this
  // component doesn't need a Suspense boundary in the layout.
  const getUrl = () => pathname + (typeof window !== 'undefined' ? window.location.search : '');

  const onUnload = useMemo(() => {
    return (url: string) => {
      Object.values(routeListeners).forEach((callback) => callback(url));
    };
  }, [routeListeners]);

  useEffect(() => onUnload(getUrl()), [onUnload, pathname]);

  useEffect(() => {
    const handleWindowUnload = () => onUnload(getUrl());
    window.addEventListener('beforeunload', handleWindowUnload);
    return () => {
      window.removeEventListener('beforeunload', handleWindowUnload);
    };
  }, [onUnload, pathname]);

  const registerRouteListener = (callback: (url: string) => void) => {
    const id = Math.random().toString(36).substring(2, 15);
    setRouteListeners((prev) => ({ ...prev, [id]: callback }));
    return id;
  };

  const unregisterRouteListener = (id: string) => {
    setRouteListeners((prev) => {
      const newListeners = { ...prev };
      delete newListeners[id];
      return newListeners;
    });
  };

  return (
    <PageUrlChangeContext.Provider value={{ registerRouteListener, unregisterRouteListener }}>
      {children}
    </PageUrlChangeContext.Provider>
  );
};
